import { useTranslation, Trans } from "react-i18next"
import { Formik, Form, Field } from "formik"
import _ from "lodash"

import { Link, useParams, useNavigate } from "../navigation"
import { DatePicker } from "../components/input/DatePicker"
import Modal from "../components/Modal"
import { saveCrudLinks, useBackend } from "../BackendProvider"
import { TagListField } from "../components/TagList"
import { isEqualTag, useTags } from "../tags/TagsProvider"
import { competitor_family } from "../data"
import { useExternal } from "./"
import { competitorFamilyUrl } from "./utils"

type ErrorEditFamily = {
    internalReference?: string
}

export default function AddEditFamily() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { internalReference } = useParams()

    const { postFamily, families } = useExternal()
    const { hasLoaded } = useBackend()
    const { tags, tagsLookup, postTag, deleteTag } = useTags()

    const isAdding = internalReference === undefined

    const availableTags = _(tags.map(t => t.tag)).uniq().sortBy(t => t.toLowerCase()).value()

    const oldFamily = families.find(f => f.internalReference === internalReference)
    const family = isAdding
        ? {
            familyName: '',
            internalReference: '',
            externalReference: '',
            summary: '',
            priorityDate: undefined,
        }
        : { ...oldFamily }

    const otherFamilyReferences = new Set(families.filter(f => f.competitorFamilyId !== family.competitorFamilyId).map(f => f.internalReference))

    const relevantTags = isAdding ? [] : tagsLookup[competitor_family]?.[family.competitorFamilyId] ?? []

    async function handleTagUpdate(newTags: string[], competitorFamilyId: number) {
        const asTagEntity = (tag: string) => ({ entityId: competitorFamilyId, entity: competitor_family, tag })
        return saveCrudLinks(relevantTags.map(asTagEntity), newTags.map(asTagEntity), postTag, deleteTag, isEqualTag)
    }

    return (hasLoaded
        ? <Modal overflowHidden={false}>
            <Formik
                initialValues={{ ...family, tags: relevantTags }}
                onSubmit={async (fam) => {
                    const saved = await postFamily({ ...fam, internalReference: fam.internalReference.trim() })
                    await handleTagUpdate(fam.tags ?? [], saved?.competitorFamilyId ?? family.competitorFamilyId)
                    navigate(competitorFamilyUrl(saved ?? fam as any))
                }}
                validate={(values) => {
                    const errors = {} as ErrorEditFamily
                    if (!(typeof values.internalReference === 'string' && values.internalReference.trim() !== ""))
                        errors.internalReference = t('reference-required')
                    else if (otherFamilyReferences.has(values.internalReference))
                        errors.internalReference = t('reference-used')
                    return errors
                }}
            >{({ touched, errors, isValid }) =>
                <Form className="max-w-4xl">
                    <h3 className="text-center p-4">{
                        isAdding
                            ? <Trans i18nKey="add-family-name" values={{ name: '' }} />
                            : <Trans i18nKey="edit-family-name" values={{ name: `${internalReference} - ${family.familyName}` }} />
                    }</h3>
                    <div className="p-4 pt-0 bg-white w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">

                        <label className="md:col-span-2 lg:col-span-3">
                            <span className="label">{t('familyName')}</span>
                            <Field name="familyName" className="form-input w-full" autoFocus />
                        </label>

                        <label>
                            <span className="label">{t('familyReference')}</span>
                            <Field
                                name="internalReference" className="form-input"
                                required pattern="[a-zA-Z0-9 ._\-]*" title={t('use-usernames')}
                            />
                            {touched.internalReference && errors.internalReference && <span className="text-red-700"><br />{errors.internalReference as string}</span>}
                        </label>

                        <label>
                            <span className="label">{t('ext-reference')}</span>
                            <Field name="externalReference" className="form-input" />
                        </label>

                        <label className="w-fit">
                            <span className="label">{t('priority-date')}</span>
                            <Field name="priorityDate" className="form-input py-1.5" as={DatePicker} />
                        </label>

                        <label className="md:col-span-2 lg:col-span-3">
                            <span className="label">{t('summary')}</span>
                            <Field name="summary" as="textarea" className="form-textarea h-48 w-full" />
                        </label>

                        <div className="sm:max-w-full md:col-span-2 lg:col-span-3">
                            <label htmlFor="tags" className="label mb-1">{t('tags')}</label>
                            <TagListField {...{
                                availableTags,
                                name: "tags",
                                primary: true,
                                allowNew: true,
                            }} />
                        </div>
                    </div>
                    <div className="p-4 bg-pc-200 flex flex-row-reverse gap-4 rounded-b-xl">
                        <input type="submit" disabled={!isValid} className="btn-primary disabled:btn-disabled" value={t("save")} />
                        <Link to=".." className="btn-secondary">{t('cancel')}</Link>
                    </div>
                </Form>
            }</Formik>
        </Modal>
        : null
    )
}
